<template>
  <div style="display: flex; gap: 40px; width: 100%">
    <div v-for="column in columns" :key="column.header" class="column">
      <div class="column-header" :class="column.headerClass">{{ column.header }}</div>
      <div class="column-card">
        <div class="field">
          <div class="field-label">KUNDENNUMMER</div>
          <div class="field-value">
            {{ column.data.id || '-' }}
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('id')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
        </div>
        <div class="field" :class="{ 'field--match': kunde.nameMatch && !column.isPreview }">
          <div class="field-label">NAME</div>
          <div class="field-value">
            {{ column.data.name || '-' }}
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('name')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
        </div>
        <div class="field">
          <div class="field-label">GEBURTSDATUM</div>
          <div class="field-value">
            {{ formatGeburtsdatum(column.data.geburtsdatum) }}
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('geburtsdatum')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
        </div>
        <div class="field" :class="{ 'field--match': kunde.emailMatch && !column.isPreview }">
          <div class="field-label">E-MAIL</div>
          <div class="field-value">
            {{ column.data.email || '-' }}
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('email')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
        </div>
        <div class="field" :class="{ 'field--match': kunde.adresseMatch && !column.isPreview }">
          <div class="field-label">ADRESSE</div>
          <div class="field-value">
            {{ column.data.adresse || '-' }}
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('adresse')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
        </div>
        <div class="field">
          <div class="field-label">KONTAKTINFORMATIONEN</div>
          <div class="contact-row" :class="{ 'field--match': kunde.telefonMobilMatch && !column.isPreview }">
            <span class="contact-label">Mobil:</span>
            <span class="contact-value">{{ column.data.telefonMobil || '-' }}</span>
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('telefonMobil')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
          <div class="contact-row" :class="{ 'field--match': kunde.telefonPrivatMatch && !column.isPreview }">
            <span class="contact-label">Privat:</span>
            <span class="contact-value">{{ column.data.telefonPrivat || '-' }}</span>
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('telefonPrivat')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
          <div
            class="contact-row"
            :class="{ 'field--match': kunde.telefonGeschaeftlichMatch && !column.isPreview }"
          >
            <span class="contact-label">Geschäftlich:</span>
            <span class="contact-value">{{ column.data.telefonGeschaeftlich || '-' }}</span>
            <span
              v-if="column.isPreview && stammtAusVerstecktemDuplikat('telefonGeschaeftlich')"
              class="hidden-duplicate-warning"
              v-b-tooltip.hover="{
                title: 'Der Wert stammt aus einem weiteren, hier nicht sichtbaren Duplikat.',
                customClass: 'tooltip-class',
              }"
            >
              <TriangleAlert :size="14" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TriangleAlert } from 'lucide-vue';

export default {
  components: { TriangleAlert },
  props: {
    kunde: { type: Object, required: true },
    duplicateKunde: { type: Object, required: true },
    mergePreview: { type: Object, default: null },
    state: { type: String, default: '' },
  },
  computed: {
    quellsaetze() {
      const kundeDate = this.kunde.master?.lastActivity ? new Date(this.kunde.master.lastActivity) : null;
      const duplicateDate = this.duplicateKunde.master?.lastActivity
        ? new Date(this.duplicateKunde.master.lastActivity)
        : null;

      const kundeDateIstAktueller = !duplicateDate || (kundeDate && kundeDate >= duplicateDate);

      return {
        aktueller: kundeDateIstAktueller ? this.kunde : this.duplicateKunde,
        aelterer: kundeDateIstAktueller ? this.duplicateKunde : this.kunde,
      };
    },
    columns() {
      const { aktueller, aelterer } = this.quellsaetze;

      const columns = [
        { header: 'Aktuellerer Kundensatz', headerClass: 'column-header--green', data: aktueller },
        { header: 'Gefundenes Duplikat', headerClass: 'column-header--yellow', data: aelterer },
      ];

      // Bei offenen Dubletten wird zusätzlich die Vorschau des zusammengeführten Kundensatzes angezeigt
      if (this.state === 'New' && this.mergePreview) {
        columns.push({
          header: 'Vorschau',
          headerClass: 'column-header--blue',
          data: this.mergePreview,
          isPreview: true,
        });
      }

      return columns;
    },
  },
  methods: {
    // Prüft, ob der Vorschauwert eines Attributs in keinem der beiden sichtbaren Sätze vorkommt
    stammtAusVerstecktemDuplikat(fieldKey) {
      if (!this.mergePreview) return false;
      const wert = this.mergePreview[fieldKey];
      if (wert === null || wert === undefined || wert === '' || wert === '-') return false;
      const { aktueller, aelterer } = this.quellsaetze;
      return wert !== aktueller[fieldKey] && wert !== aelterer[fieldKey];
    },
    formatGeburtsdatum(value) {
      return value ? this.$options.filters.date(value) : '-';
    },
  },
};
</script>

<style lang="scss" scoped>
.column {
  flex: 1;
  min-width: 0;
}

.column-header {
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 24px 16px;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 16px;

  &--green {
    background-color: #f0fdf4;
    color: #016630;
    border: 1px solid #c8e6c9;
  }

  &--yellow {
    background-color: #fffbeb;
    color: #973c00;
    border: 1px solid #ffc107;
  }

  &--blue {
    background-color: #eff6ff;
    color: #1e40af;
    border: 1px solid #bfdbfe;
  }
}

.column-card {
  border: 0.5px solid var(--border);
  border-radius: var(--radius-md);
  padding: 24px;
  background-color: var(--surface);
  box-shadow: 0px 1px 3px 0px #0000001a;
}

.field {
  margin-bottom: 16px;
}

.field-label {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.field-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
}

.contact-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-muted);
}

.contact-label {
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 400;
  min-width: 90px;
}

.contact-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

.field--match {
  background-color: rgba(#1bc5bd, 0.1);
  border-radius: 4px;
  padding: 2px 0px;
}

// Warn-Icon für Werte
.hidden-duplicate-warning {
  display: inline-flex;
  align-self: center;
  color: var(--warning);
  cursor: pointer;
}
</style>

<style>
.tooltip-class.tooltip {
  opacity: 1;
}
</style>
